import React, { useState } from 'react';
import { ReviewDiscussionMessage } from '../../types/pms';
import './ReviewDiscussion.css';

interface ReviewDiscussionProps {
  messages: ReviewDiscussionMessage[];
  /** Which side the viewer is on — decides alignment and the "You" label. */
  viewerRole: 'employee' | 'manager';
  onSend?: (text: string) => void | Promise<void>;
  sending?: boolean;
  /** Why posting is unavailable, when it is. Shown instead of the composer. */
  disabledReason?: string;
  error?: string;
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Employee ↔ manager conversation about one review.
 *
 * Deliberately a plain append-only thread, and deliberately NOT wired to any of the manager's
 * evaluation fields. A message here is somebody talking; overallComments / managerFinalComment /
 * ObjectiveRating.comments are the manager's formal verdict. The review-isolation work already
 * had to unpick one case where those got crossed and an employee's note rendered under a manager
 * heading, so the two are kept in separate storage and separate UI — see
 * ReviewMetadata.discussion.
 *
 * No edit or delete: the other side may already have read and replied to a message, so letting
 * either party rewrite history would make the thread untrustworthy as a record.
 */
const ReviewDiscussion: React.FC<ReviewDiscussionProps> = ({
  messages,
  viewerRole,
  onSend,
  sending = false,
  disabledReason,
  error,
}) => {
  const [draft, setDraft] = useState('');

  const submit = async () => {
    const text = draft.trim();
    if (!text || sending || !onSend) return;
    try {
      await onSend(text);
      // Cleared only once the caller has resolved, so a failed post does not throw away what
      // the person typed — they can correct the problem and press Send again.
      setDraft('');
    } catch {
      // The caller surfaces the failure through `error`; swallowed here so a rejected send
      // does not become an unhandled rejection.
    }
  };

  return (
    <div className="rd">
      {messages.length === 0 ? (
        <p className="rd__empty">
          No messages yet. Use this thread to discuss the review — it is separate from the
          manager's formal evaluation comments.
        </p>
      ) : (
        <ol className="rd__list">
          {messages.map((message) => {
            const isMine = message.authorRole === viewerRole;
            return (
              <li
                key={message.id}
                className={`rd__row ${isMine ? 'rd__row--mine' : ''} rd__row--${message.authorRole}`}
              >
                <div className="rd__bubble">
                  <div className="rd__meta">
                    <span className="rd__author">
                      {message.authorName}
                      {isMine && <span className="rd__you"> · You</span>}
                    </span>
                    <span className="rd__role">
                      {message.authorRole === 'manager' ? 'Manager' : 'Employee'}
                    </span>
                  </div>
                  <p className="rd__text">{message.text}</p>
                  <time className="rd__time" dateTime={message.timestamp}>
                    {formatStamp(message.timestamp)}
                  </time>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {error && <p className="rd__error" role="alert">{error}</p>}

      {onSend && !disabledReason && (
        <div className="rd__composer">
          <textarea
            className="rd__textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter makes a new line — the convention people already
              // expect from a message box.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Write a message…"
            rows={2}
            disabled={sending}
            aria-label="Discussion message"
          />
          <button
            type="button"
            className="rd__send"
            onClick={submit}
            disabled={sending || !draft.trim()}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}

      {disabledReason && <p className="rd__disabled">{disabledReason}</p>}
    </div>
  );
};

export default ReviewDiscussion;
